import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Youtube, RefreshCw, CheckCircle2, AlertTriangle, Copy, Download } from "lucide-react";

const LEVELS = [
  { id: "calm", label: "Calm", tone: "#2a9d3a" },
  { id: "watching", label: "Watching", tone: "#eab308" },
  { id: "active", label: "Active", tone: "#f6a21c" },
  { id: "dangerous", label: "Dangerous", tone: "#d90429" },
];
const Label = ({ children }) => <div className="mb-1 font-oswald text-[11px] uppercase tracking-widest text-gray-400">{children}</div>;
const Toggle = ({ checked, onChange, testId, label }) => (
  <label className="flex items-center gap-2 text-sm text-gray-200">
    <input type="checkbox" data-testid={testId} checked={!!checked} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 accent-[#1f63c9]" />
    {label}
  </label>
);

export function YouTubeMetaCard() {
  const [cfg, setCfg] = useState(null);
  const [status, setStatus] = useState(null);
  const [templates, setTemplates] = useState({});
  const [vid, setVid] = useState("");
  const [busy, setBusy] = useState(false);

  const loadStatus = () => api.get("/yt-meta/status").then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/yt-meta/config").then((r) => { setCfg(r.data); setTemplates(r.data.templates || {}); setVid(r.data.video_id_override || ""); }).catch(() => {});
    loadStatus();
    const t = setInterval(loadStatus, 20000);
    return () => clearInterval(t);
  }, []);

  const save = async (body, msg) => {
    try { const { data } = await api.put("/yt-meta/config", body); setCfg(data); if (msg) toast.success(msg); loadStatus(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const syncNow = async () => {
    setBusy(true);
    try { const { data } = await api.post("/yt-meta/sync"); toast.success(data.changed ? `Applied: ${data.title}` : "Already up to date"); loadStatus(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Sync failed"); } finally { setBusy(false); }
  };
  const copyTitle = async () => {
    const t = status?.preview_title;
    if (!t) return;
    try { await navigator.clipboard.writeText(t); toast.success("Title copied — paste it into YouTube Studio"); }
    catch { toast.error("Couldn't copy to clipboard"); }
  };
  const downloadThumb = async (level) => {
    try {
      const { data } = await api.get(`/yt-meta/preview/${level}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = `gawx-thumbnail-${level}.jpg`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  if (!cfg) return null;
  const connected = cfg.creds_configured;

  return (
    <div className="glass rounded-sm border border-red-500/40 p-6" data-testid="yt-meta-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Youtube className="h-5 w-5 text-red-400" />
          <div className="font-oswald text-lg font-bold uppercase tracking-widest text-white">Auto Title &amp; Thumbnail</div>
        </div>
        <Toggle testId="yt-meta-enabled" checked={cfg.enabled} onChange={(v) => save({ enabled: v }, v ? "Auto-updates on" : "Auto-updates off")} label={cfg.enabled ? "On" : "Off"} />
      </div>
      <p className="mt-1 text-sm text-gray-300">Your stream's title and thumbnail update themselves as the master Threat Level shifts (Calm → Watching → Active → Dangerous). Writes fire only when the level changes.</p>

      {/* Connection status */}
      <div className={`mt-4 flex items-start gap-2 rounded-sm border px-4 py-3 text-sm ${connected ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200" : "border-amber-500/40 bg-amber-500/10 text-amber-200"}`} data-testid="yt-meta-conn">
        {connected ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" /> : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />}
        <div>
          {connected ? (
            <>Channel connected. Target video: <span className="font-mono text-white">{status?.video_id || cfg.video_id_override || status?.stream_video_id || "auto-detect active broadcast"}</span></>
          ) : (
            <>Channel not connected yet. Add your YouTube OAuth secrets (<span className="font-mono">YOUTUBE_OAUTH_CLIENT_ID</span>, <span className="font-mono">YOUTUBE_OAUTH_CLIENT_SECRET</span>, <span className="font-mono">YOUTUBE_OAUTH_REFRESH_TOKEN</span>) to enable auto-apply. The builder will walk you through the one-time sign-in.</>
          )}
        </div>
      </div>

      {/* Manual mode — no sign-in needed */}
      <div className="mt-4 rounded-sm border border-[#1f63c9]/40 bg-[#1f63c9]/10 px-4 py-3" data-testid="yt-meta-manual">
        <div className="font-oswald text-xs uppercase tracking-widest text-[#8fb6ef]">Manual mode · no sign-in needed</div>
        <p className="mt-1 text-sm text-gray-300">Copy the current title and download the matching thumbnail, then paste/upload them in YouTube Studio.{connected ? " Or just leave auto-updates on above." : ""}</p>
        <div className="mt-3 flex flex-wrap gap-3">
          <button data-testid="yt-meta-copy-title" onClick={copyTitle} className="flex items-center gap-2 rounded-sm border border-white/15 bg-black/40 px-4 py-2 font-oswald text-sm uppercase tracking-widest text-white hover:border-[#1f63c9]"><Copy className="h-4 w-4" /> Copy current title</button>
          <button data-testid="yt-meta-download-current" onClick={() => downloadThumb(status?.current_level || "calm")} className="flex items-center gap-2 rounded-sm border border-white/15 bg-black/40 px-4 py-2 font-oswald text-sm uppercase tracking-widest text-white hover:border-[#1f63c9]"><Download className="h-4 w-4" /> Download current thumbnail</button>
        </div>
      </div>

      {/* Live state */}
      {status && (
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <div className="rounded-sm border border-white/10 bg-black/30 px-4 py-3">
            <Label>Current Threat Level</Label>
            <div className="font-oswald text-2xl font-black uppercase" style={{ color: LEVELS.find((l) => l.id === status.current_level)?.tone || "#fff" }} data-testid="yt-meta-current-level">{status.current_label || "—"}</div>
            <div className="mt-1 text-xs text-gray-400">{status.current_reason}</div>
          </div>
          <div className="rounded-sm border border-white/10 bg-black/30 px-4 py-3">
            <Label>Title that would air now</Label>
            <div className="text-sm text-white" data-testid="yt-meta-preview-title">{status.preview_title || "—"}</div>
            {status.last_applied_at && <div className="mt-1 text-xs text-gray-400">Last applied: {LEVELS.find((l) => l.id === status.last_level)?.label} · {new Date(status.last_applied_at).toLocaleString()}</div>}
            {status.last_error && <div className="mt-1 text-xs text-red-400">Last error: {status.last_error}</div>}
          </div>
        </div>
      )}

      {/* Options */}
      <div className="mt-4 flex flex-wrap items-center gap-6">
        <Toggle testId="yt-meta-thumb" checked={cfg.update_thumbnail} onChange={(v) => save({ update_thumbnail: v }, "Saved")} label="Also swap the thumbnail" />
        <Toggle testId="yt-meta-reason" checked={cfg.append_reason} onChange={(v) => save({ append_reason: v }, "Saved")} label="Append the live reason to severe titles" />
      </div>

      {/* Video id override */}
      <div className="mt-4 max-w-md">
        <Label>Target video ID <span className="text-gray-500">· optional, defaults to your 24/7 stream</span></Label>
        <input data-testid="yt-meta-video-id" value={vid} onChange={(e) => setVid(e.target.value)} onBlur={() => vid !== (cfg.video_id_override || "") && save({ video_id_override: vid }, "Video ID saved")}
          placeholder="auto-detect active broadcast" className="w-full rounded-sm border border-white/15 bg-black/40 px-3 py-2 font-mono text-sm text-white placeholder:text-gray-600 focus:border-[#1f63c9] focus:outline-none" />
      </div>

      {/* Title templates */}
      <div className="mt-5">
        <Label>Title per level <span className="text-gray-500">· max 100 chars</span></Label>
        <div className="grid gap-3 md:grid-cols-2">
          {LEVELS.map((l) => (
            <div key={l.id}>
              <div className="mb-1 flex items-center gap-2"><span className="h-3 w-3 rounded-full" style={{ background: l.tone }} /><span className="font-oswald text-xs uppercase tracking-widest text-gray-300">{l.label}</span></div>
              <input data-testid={`yt-meta-tmpl-${l.id}`} value={templates[l.id] || ""} maxLength={100}
                onChange={(e) => setTemplates({ ...templates, [l.id]: e.target.value })}
                onBlur={() => templates[l.id] !== (cfg.templates?.[l.id] || "") && save({ templates: { ...templates } }, `${l.label} title saved`)}
                className="w-full rounded-sm border border-white/15 bg-black/40 px-3 py-2 text-sm text-white focus:border-[#1f63c9] focus:outline-none" />
            </div>
          ))}
        </div>
      </div>

      {/* Thumbnail previews */}
      <div className="mt-5">
        <Label>Threat-level thumbnails</Label>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {LEVELS.map((l) => (
            <div key={l.id} className="overflow-hidden rounded-sm border border-white/10" data-testid={`yt-meta-thumb-${l.id}`}>
              <img src={`${API}/yt-meta/preview/${l.id}`} alt={`${l.label} thumbnail`} className="aspect-video w-full object-cover" loading="lazy" />
              <div className="flex items-center justify-between px-2 py-1" style={{ borderTop: `2px solid ${l.tone}` }}>
                <span className="font-oswald text-[11px] uppercase tracking-widest text-gray-300">{l.label}</span>
                <button onClick={() => downloadThumb(l.id)} data-testid={`yt-meta-dl-${l.id}`} title="Download" className="text-gray-400 hover:text-white"><Download className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Sync now */}
      <div className="mt-5 flex items-center gap-3">
        <button data-testid="yt-meta-sync" onClick={syncNow} disabled={!connected || busy}
          className="flex items-center gap-2 rounded-sm bg-[#1f63c9] px-4 py-2 font-oswald text-sm font-bold uppercase tracking-widest text-white hover:bg-[#2a74d8] disabled:cursor-not-allowed disabled:opacity-40">
          <RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} /> Sync now
        </button>
        <span className="text-xs text-gray-400">Pushes the current level's title + thumbnail immediately.</span>
      </div>
    </div>
  );
}
